import React, {useMemo} from 'react'
import {css} from '@emotion/react'
import styled from '@emotion/styled'

import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import {
  getRoundingStrategy,
  RoundingStrategy
} from '@daedalus/core/src/utils/number'

import {cssTheme} from '../../../themes'
import {Price, PriceSize} from '../Price'

interface Props {
  /** Whether the price should be styled as an anchor price */
  anchorPrice: number
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Price currency code */
  currencyCode: string
  /** The price should be styled as a private deal */
  isPrivateDeal?: boolean
  /** Whether the price is hidden as it is locked */
  isLockedDeal?: boolean
  /** Whether the price is an app lock deal */
  isAppLockedDeal?: boolean
  /** The value to be displayed as the price */
  price: number
  /** Whether we should show an anchor price */
  showAnchorPrice?: boolean
  /** Whether the locked icon can be shown. It is only shown when isPrivateDeal is also true. */
  showLockIcon?: boolean
  /** Whether the locked icon can be shown. It is only shown when isAppLockedDeal is also true. */
  showAppIcon?: boolean
  /** Whether the elements should be displayed in a row layout */
  showRowLayout?: boolean
  /** Whether the element should be displayed inline to its siblings ie; not flex */
  shouldDisplayInline?: boolean
  /** Price Size */
  size?: PriceSize
  /** Shows the indication by the side of the price */
  showIndication?: boolean
  /** Shows the indication that the price is total by the side of the price */
  showTotal?: boolean
  /** Rooms configuration */
  numberOfRooms?: number
  /** Total number of guests */
  numberOfGuests?: number
  /** Shows the CTAOffer  */
  shouldAlignOffer?: boolean
  /** Rounding strategy to apply to the price */
  roundingStrategy?: RoundingStrategy
}

const PriceWrapper = styled.div<{
  showRowLayout?: boolean
  shouldDisplayInline?: boolean
}>(({showRowLayout, shouldDisplayInline, theme}) =>
  shouldDisplayInline
    ? css`
        display: inline-block;
      `
    : showRowLayout
      ? css`
          display: flex;
          flex-direction: row;
          align-items: baseline;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: ${cssTheme.layout.spacing.s200};
        `
      : css`
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        `
)
const getMessageWrapperStylesBySize = (size?: PriceSize) => {
  switch (size) {
    case 'xxl':
      return css`
        display: inline-block;
        color: ${cssTheme.colors.content.neutral.c950};
      `
    case 'sm':
      return css`
        padding-bottom: ${cssTheme.layout.spacing.s100};
        padding-left: ${cssTheme.layout.spacing.s200};
        color: ${cssTheme.colors.content.neutral.c600};
      `
    default:
      return css`
        padding-bottom: ${cssTheme.layout.spacing.s200};
        padding-left: ${cssTheme.layout.spacing.s200};
        color: ${cssTheme.colors.content.neutral.c600};
      `
  }
}

export const MessageWrapper = styled.div<{size?: PriceSize}>(
  ({size}) => css`
    ${getMessageWrapperStylesBySize(size)}
    ${cssTheme.typography.text.bodyXS};
    white-space: nowrap;
    text-transform: lowercase;
  `
)

export const PriceAndMessageWrapper = styled.div`
  display: flex;
  align-items: flex-end;
`

export const GuestMessage = styled.span`
  text-transform: lowercase;
`

export const PriceComposition = ({
  price,
  anchorPrice,
  currencyCode,
  isPrivateDeal = false,
  isLockedDeal = false,
  showRowLayout,
  shouldDisplayInline = false,
  showAnchorPrice = false,
  showLockIcon = true,
  showAppIcon = false,
  isAppLockedDeal = false,
  size,
  showIndication = false,
  showTotal = false,
  shouldAlignOffer = false,
  roundingStrategy = getRoundingStrategy(),
  className,
  numberOfRooms = 1
}: Props) => {
  const renderPriceIndication = useMemo(() => {
    if (showIndication) {
      // Shows the indication that the price is total by the side of the price
      if (showTotal) {
        return (
          <MessageWrapper size={size}>
            / <FormattedMessageWrapper id="total" defaultMessage="total" />
          </MessageWrapper>
        )
      }

      // Shows the indication that the price is per night per room by the side of the price
      if (numberOfRooms > 1) {
        return (
          <MessageWrapper size={size}>
            /{' '}
            <FormattedMessageWrapper
              id="nightPerRoom"
              defaultMessage="night per room"
            />
          </MessageWrapper>
        )
      }

      // Shows the indication that the price is per night by the side of the price
      return (
        <MessageWrapper size={size}>
          <FormattedMessageWrapper id="perNight" defaultMessage="/ night" />
        </MessageWrapper>
      )
    }

    return null
  }, [size, showIndication, showTotal, numberOfRooms])

  return (
    <PriceWrapper
      showRowLayout={showRowLayout}
      shouldDisplayInline={shouldDisplayInline}
      className={className}
    >
      {showAnchorPrice && anchorPrice > 0 && (
        <Price
          isAnchor
          value={anchorPrice}
          currency={currencyCode}
          roundingStrategy={roundingStrategy}
          shouldAlignOffer={shouldAlignOffer}
        />
      )}
      <PriceAndMessageWrapper>
        <Price
          value={price}
          currency={currencyCode}
          isPrivate={isPrivateDeal}
          isLocked={isLockedDeal}
          isAppLocked={isAppLockedDeal}
          roundingStrategy={roundingStrategy}
          showLockIcon={showLockIcon}
          showAppIcon={showAppIcon}
          size={size}
          shouldAlignOffer={shouldAlignOffer}
        />
        {renderPriceIndication}
      </PriceAndMessageWrapper>
    </PriceWrapper>
  )
}
